// src/components/AddToCartSelectorDrawer.js
import React, {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { LanguageContext } from "../context/LanguageContext";
import { useCart } from "../context/CartContext";
import { flyImageToCart } from "../utils/flyToCart";
import { getProductBySlug } from "../services/products.api";
import { apiRequest } from "../services/api";

function AddToCartSelectorDrawer({
  isOpen,
  onClose,
  product,
  selectedSize,
  selectedColor,
  setSelectedSize,
  setSelectedColor,
}) {
  const { lang } = useContext(LanguageContext);
  const { addToCart } = useCart();
  const imgRef = useRef(null);

  const [fullProduct, setFullProduct] = useState(null);

  /* ---------------- SAFE PRODUCT ---------------- */
  const baseProduct = product || {};

  /* ---------------- LOAD FULL PRODUCT ---------------- */
  useEffect(() => {
    if (!isOpen || !baseProduct.slug) return;

    getProductBySlug(baseProduct.slug)
      .then(setFullProduct)
      .catch(console.error);
  }, [isOpen, baseProduct.slug]);

  const p = fullProduct || baseProduct;

  /* ---------------- BASIC INFO ---------------- */
  const displayName =
    lang === "EN" ? p.name_en ?? "" : p.name_ar ?? "";

  const mainImage =
    p.images?.[0]?.url ??
    p.images?.[0] ??
    p.image ??
    "/images/placeholder-product.jpg";

  const hasDiscount = Boolean(p.on_sale && Number(p.sale_percent) > 0);
  const basePrice = Number(p.base_price || p.price || 0);

  const finalPrice = hasDiscount
    ? Math.round(basePrice * (1 - p.sale_percent / 100))
    : basePrice;

  /* ---------------- COLORS ---------------- */
  const colors = useMemo(() => {
    if (!Array.isArray(p.variants)) return [];

    const map = new Map();
    p.variants.forEach((v) => {
      if (!map.has(v.color_name)) {
        map.set(v.color_name, v.color_hex || "#000");
      }
    });

    return Array.from(map.entries()).map(([name, code]) => ({
      name,
      code,
    }));
  }, [p]);

  /* ---------------- ALL SIZES (ALWAYS VISIBLE) ---------------- */
  const allSizes = useMemo(() => {
    if (!Array.isArray(p.variants)) return [];

    const map = new Map();
    p.variants.forEach((v) => {
      const key = String(v.size);
      if (!map.has(key)) {
        map.set(key, {
          size: v.size,
          variants: [],
        });
      }
      map.get(key).variants.push(v);
    });

    return Array.from(map.values()).sort(
      (a, b) => Number(a.size) - Number(b.size)
    );
  }, [p]);

  /* ---------------- SELECTED VARIANT ---------------- */
  const selectedVariant = useMemo(() => {
    if (!selectedColor || !selectedSize) return null;

    return (
      p.variants?.find(
        (v) =>
          String(v.size) === String(selectedSize) &&
          v.color_name === selectedColor &&
          Number(v.stock_qty) > 0
      ) || null
    );
  }, [p, selectedColor, selectedSize]);

  const canAdd = Boolean(selectedVariant);

  /* ---------------- ACTION ---------------- */
  const handleAdd = () => {
    if (!canAdd) return;

    if (imgRef.current) flyImageToCart(imgRef.current);

    apiRequest("/analytics/track", {
  method: "POST",
  body: JSON.stringify({
    action_type: "add_to_cart",
    entity_type: "product",
    entity_id: p.id,
  }),
}).catch(() => {});

if (typeof window.fbq === "function") {
  window.fbq("track", "AddToCart", {
    content_ids: [p.public_id],
    content_type: "product",
    value: finalPrice,
    currency: "USD",
  });
}


addToCart({
  product_id: p.id,
  variant_id: selectedVariant.id,
  public_id: p.public_id,
  nameEN: p.name_en,
  nameAR: p.name_ar,
  price: finalPrice + Number(selectedVariant.extra_price || 0),
  image: mainImage,
  size: selectedVariant.size,
  color: selectedVariant.color_name,
  color_hex: selectedVariant.color_hex || "#000000",
});


    onClose();
  };

  if (!product) return null;

  return (
    <div
      className={`fixed inset-0 z-[999999] ${
        isOpen ? "pointer-events-auto" : "pointer-events-none"
      }`}
    >
      {/* BACKDROP */}
      <div
        className={`absolute inset-0 bg-black/40 ${
          isOpen ? "opacity-100" : "opacity-0"
        }`}
        onClick={onClose}
      />

      {/* DRAWER */}
      <div
        className={`absolute left-0 right-0 bottom-0 bg-white rounded-t-2xl px-5 pt-5 pb-6 transition-transform ${
          isOpen ? "translate-y-0" : "translate-y-full"
        }`}
      >
        {/* HEADER */}
        <div className="flex justify-between mb-4">
          <h2 className="font-semibold">
            {lang === "EN" ? "Select Options" : "اختاري الخيارات"}
          </h2>
          <button onClick={onClose} className="text-2xl opacity-50">
            ×
          </button>
        </div>

        {/* PREVIEW */}
        <div className="flex gap-3 mb-5">
          <img
            ref={imgRef}
            src={mainImage}
            alt={displayName}
            className="w-20 h-24 object-cover rounded-md border"
          />
          <div>
  <p className="font-semibold">{displayName}</p>

  {p.public_id && (
    <p className="text-[11px] text-[#9F9087] mt-[2px]">
      {p.public_id}
    </p>
  )}

  <p className="font-bold">${finalPrice}</p>
</div>

        </div>

        {/* COLORS */}
        <p className="text-sm font-semibold mb-2">
          {lang === "EN" ? "Color" : "اللون"}
        </p>
        <div className="flex gap-3 mb-5">
          {colors.map((c) => (
            <button
              key={c.name}
              onClick={() => {
                setSelectedColor(c.name);
                setSelectedSize(null);
              }}
              className={`w-8 h-8 rounded-full border flex items-center justify-center ${
                selectedColor === c.name
                  ? "border-black"
                  : "border-[#D3C5BE]"
              }`}
              style={{ backgroundColor: c.code }}
            />
          ))}
        </div>

        {/* SIZES (ALWAYS VISIBLE) */}
        <p className="text-sm font-semibold mb-2">
          {lang === "EN" ? "Size" : "المقاس"}
        </p>
        <div className="flex flex-wrap gap-2 mb-6">
          {allSizes.map((s) => {
            const availableForColor = selectedColor
              ? s.variants.some(
                  (v) =>
                    v.color_name === selectedColor &&
                    Number(v.stock_qty) > 0
                )
              : false;

            return (
              <button
                key={s.size}
                disabled={!selectedColor || !availableForColor}
                onClick={() => setSelectedSize(s.size)}
                className={`px-3 py-1 rounded-md border text-sm
                  ${
                    selectedSize === s.size
                      ? "bg-black text-white border-black"
                      : "border-[#D3C5BE]"
                  }
                  ${
                    !selectedColor || !availableForColor
                      ? "opacity-30 cursor-not-allowed"
                      : ""
                  }
                `}
              >
                {s.size}
              </button>
            );
          })}
        </div>

        {/* ACTIONS */}
        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 py-3 border rounded-md font-semibold"
          >
            {lang === "EN" ? "CANCEL" : "إلغاء"}
          </button>

          <button
            onClick={handleAdd}
            disabled={!canAdd}
            className={`flex-1 py-3 rounded-md font-semibold ${
              canAdd ? "bg-black text-white" : "bg-gray-200"
            }`}
          >
            {lang === "EN" ? "ADD" : "أضف"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default AddToCartSelectorDrawer;
